import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../store";
import { REPORTS_STALE_EVENT } from "../util/reportEvents";
import { forgetPushSubscription, getBrowserSubscription, pushSupported, subscribeBrowser } from "../util/push";

/**
 * unsupported / unconfigured: nothing to offer; off: can be turned on; on: this browser receives them;
 * denied: blocked in the browser's settings, which only the user can undo
 */
export type PushState = "unsupported" | "unconfigured" | "off" | "on" | "denied";

// The key is the same for everyone using this API, so it is asked for once per page load
let publicKeyRequest: Promise<string | null> | undefined;

/** Browser notifications for new messages and follow-ups that turned due, delivered by the server through Web Push */
export const usePushNotifications = () => {
  const { invokeApi } = useUser();
  const navigate = useNavigate();
  const [state, setState] = useState<PushState>(pushSupported() ? "off" : "unsupported");
  const [busy, setBusy] = useState(false);

  const getPublicKey = useCallback(() => {
    publicKeyRequest ??= invokeApi({ endpoint: "/getPushConfig", silent: true })
      .then((config: any) => config?.publicKey ?? null)
      .catch(() => {
        publicKeyRequest = undefined;
        return null;
      });
    return publicKeyRequest;
  }, [invokeApi]);

  useEffect(() => {
    if (!pushSupported()) return;
    let cancelled = false;
    (async () => {
      const publicKey = await getPublicKey();
      if (cancelled) return;
      if (!publicKey) return setState("unconfigured");
      if (Notification.permission === "denied") return setState("denied");

      const subscription = await getBrowserSubscription();
      if (cancelled) return;
      if (Notification.permission === "granted" && subscription) {
        setState("on");
        // The browser may be shared or the record lost: make sure the server knows it belongs to this user
        invokeApi({ endpoint: "/subscribePush", data: { subscription: subscription.toJSON() }, silent: true }).catch(() => undefined);
      } else {
        setState("off");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getPublicKey, invokeApi]);

  // Messages from the service worker: something changed on the server, or a notification was clicked
  useEffect(() => {
    if (!pushSupported()) return;
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === "reports-stale") window.dispatchEvent(new Event(REPORTS_STALE_EVENT));
      if (event.data?.type === "open" && typeof event.data.url === "string" && event.data.url.startsWith("/")) {
        navigate(event.data.url);
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [navigate]);

  const enable = useCallback(async () => {
    setBusy(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") return setState(permission === "denied" ? "denied" : "off");
      const publicKey = await getPublicKey();
      if (!publicKey) return setState("unconfigured");

      const subscription = await subscribeBrowser(publicKey);
      await invokeApi({ endpoint: "/subscribePush", data: { subscription: subscription.toJSON() } });
      setState("on");
    } catch (error) {
      console.error("Could not turn on notifications", error);
      setState("off");
    } finally {
      setBusy(false);
    }
  }, [getPublicKey, invokeApi]);

  const disable = useCallback(async () => {
    setBusy(true);
    try {
      const subscription = await getBrowserSubscription();
      await forgetPushSubscription(invokeApi);
      await subscription?.unsubscribe();
      setState("off");
    } finally {
      setBusy(false);
    }
  }, [invokeApi]);

  return { state, busy, enable, disable };
};
